import { type SyntheticEvent, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { Mail, Sparkles, User, CircleCheck, CircleX } from 'lucide-react';
import { AuthLayout, AuthInput, AuthButton } from './AuthComponents';
import { useAuth } from '@/context/AuthContext';
import { UPDATE_PROFILE } from '@/graphql/mutations';
import { CHECK_USERNAME_AVAILABILITY } from '@/graphql/queries';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Gender } from '@/gql/graphql';
import { cn } from '@/lib/utils';

export function CompleteProfile() {
    const navigate = useNavigate();
    const { user, login } = useAuth();
    const { t } = useTranslation('auth');
    const [updateProfile, { loading }] = useMutation(UPDATE_PROFILE);
    const [error, setError] = useState('');

    const isPlaceholderEmail = user?.email.endsWith('@placeholder.local') ?? false;

    const [formData, setFormData] = useState({
        email: isPlaceholderEmail ? '' : (user?.email ?? ''),
        username: user?.username ?? '',
        gender: user?.gender ?? Gender.Other,
        birthDate: '',
    });

    // Username availability check
    const [debouncedUsername] = useDebounce(formData.username, 500);
    const originalUsername = user?.username ?? '';
    const usernameChanged = debouncedUsername.toLowerCase() !== originalUsername.toLowerCase();

    const { data: usernameCheck, loading: checkingUsername } = useQuery(
        CHECK_USERNAME_AVAILABILITY,
        {
            variables: { username: debouncedUsername },
            skip: debouncedUsername.length < 3 || !usernameChanged,
        },
    );
    const usernameAvailable = !usernameChanged || usernameCheck?.checkUsernameAvailability;

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();
        setError('');

        if (isPlaceholderEmail && !formData.email) {
            setError(t('completeProfile.error.emailRequired'));
            return;
        }

        if (!formData.birthDate) {
            setError(t('completeProfile.error.birthDateRequired'));
            return;
        }

        if (usernameChanged && !usernameAvailable) {
            setError(t('completeProfile.error.usernameTaken'));
            return;
        }

        try {
            const dateObj = new Date(formData.birthDate);
            const isoDate = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate(), 12, 0, 0)).toISOString();

            const input: Record<string, string> = {
                birthDate: isoDate,
                gender: formData.gender,
            };
            if (isPlaceholderEmail) {
                input.email = formData.email;
            }
            if (usernameChanged) {
                input.username = formData.username;
            }

            await updateProfile({ variables: { input } });

            // Refetch profile to update AuthContext
            const token = localStorage.getItem('auth_token');
            if (token) {
                await login(token);
            }

            navigate('/races', { replace: true });
        } catch (err: unknown) {
            console.error(err);
            if (err instanceof Error && err.message.includes('Duplicate')) {
                setError(t('completeProfile.error.emailTaken'));
            } else if (err instanceof Error && err.message.includes('Username')) {
                setError(t('completeProfile.error.usernameTaken'));
            } else {
                setError(t('completeProfile.error.generic'));
            }
        }
    };

    const inputClass = "w-full h-14 px-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium text-slate-900";

    return (
        <AuthLayout
            title={t('completeProfile.title')}
            subtitle={t('completeProfile.subtitle')}
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {isPlaceholderEmail && (
                    <AuthInput
                        label={t('completeProfile.email.label')}
                        icon={Mail}
                        name="email"
                        type="email"
                        placeholder={t('completeProfile.email.placeholder')}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                )}

                {/* Username */}
                <div className="space-y-2">
                    <AuthInput
                        label={t('completeProfile.username.label')}
                        icon={User}
                        name="username"
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                    {usernameChanged && debouncedUsername.length >= 3 && (
                        <div className="flex items-center gap-1.5 ml-1">
                            {checkingUsername ? (
                                <span className="text-xs text-slate-400">{t('completeProfile.username.checking')}</span>
                            ) : usernameAvailable ? (
                                <>
                                    <CircleCheck size={14} className="text-emerald-500" />
                                    <span className="text-xs text-emerald-600 font-medium">{t('completeProfile.username.available')}</span>
                                </>
                            ) : (
                                <>
                                    <CircleX size={14} className="text-red-500" />
                                    <span className="text-xs text-red-600 font-medium">{t('completeProfile.username.taken')}</span>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Gender + Birth date */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-slate-700 ml-1">{t('completeProfile.gender.label')}</label>
                        <select
                            className={cn(inputClass, "appearance-none")}
                            value={formData.gender}
                            onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                        >
                            <option value={Gender.Male}>{t('completeProfile.gender.male')}</option>
                            <option value={Gender.Female}>{t('completeProfile.gender.female')}</option>
                            <option value={Gender.Other}>{t('completeProfile.gender.other')}</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-slate-700 ml-1">{t('completeProfile.birthDate.label')}</label>
                        <input
                            type="date"
                            className={inputClass}
                            max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            value={formData.birthDate}
                            onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                        />
                    </div>
                </div>

                {error && <ErrorBanner message={error} className="animate-in shake" />}

                <AuthButton
                    type="submit"
                    isLoading={loading}
                    disabled={usernameChanged && !usernameAvailable}
                >
                    {t('completeProfile.submit')} <Sparkles size={20} />
                </AuthButton>
            </form>
        </AuthLayout>
    );
}
